import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCompany, PageHeader, Money } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtDate } from "@/lib/format";
import { Loader2, BookOpen } from "lucide-react";

export default function Accounting() {
  const { companyId, cur } = useCompany();
  const [entries, setEntries] = useState(null);
  const [balance, setBalance] = useState(null);
  useEffect(() => {
    if (!companyId) return;
    api.get("/accounting/entries", { params: { company_id: companyId } }).then(({ data }) => setEntries(data.entries));
    api.get("/accounting/balance", { params: { company_id: companyId } }).then(({ data }) => setBalance(data));
  }, [companyId]);

  return (
    <div data-testid="accounting-page">
      <PageHeader title="Comptabilité" subtitle="Écritures générées automatiquement (partie double, SYSCOHADA)." />
      <Tabs defaultValue="entries">
        <TabsList className="mb-4"><TabsTrigger value="entries" data-testid="tab-entries">Journal général</TabsTrigger><TabsTrigger value="balance" data-testid="tab-balance">Balance</TabsTrigger></TabsList>

        <TabsContent value="entries">
          <Card className="rounded-md border-border shadow-sm overflow-hidden">
            {entries === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
              : entries.length === 0 ? <p className="p-6 text-sm text-muted-foreground">Aucune écriture. Créez une facture ou une dépense pour générer la comptabilité.</p>
              : <div className="divide-y divide-border">{entries.map((e) => (
                  <div key={e.id} className="p-4" data-testid={`je-row-${e.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium flex items-center gap-2"><BookOpen className="w-4 h-4 text-primary" />{e.description}</div>
                      <div className="text-xs text-muted-foreground">{fmtDate(e.entry_date)} · journal {e.journal}</div>
                    </div>
                    <div className="ml-6 text-sm">
                      {e.lines.map((l, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 py-0.5">
                          <span className="col-span-2 font-mono text-xs text-muted-foreground">{l.account_code}</span>
                          <span className="col-span-6">{l.account_name}</span>
                          <span className="col-span-2 text-right tabular">{l.debit ? <Money value={l.debit} cur={cur} /> : ""}</span>
                          <span className="col-span-2 text-right tabular text-muted-foreground">{l.credit ? <Money value={l.credit} cur={cur} /> : ""}</span>
                        </div>))}
                    </div>
                  </div>))}
                </div>}
          </Card>
        </TabsContent>

        <TabsContent value="balance">
          <Card className="rounded-md border-border shadow-sm overflow-hidden">
            {balance === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
              : <Table>
                  <TableHeader><TableRow><TableHead>Compte</TableHead><TableHead>Libellé</TableHead><TableHead className="text-right">Débit</TableHead><TableHead className="text-right">Crédit</TableHead><TableHead className="text-right">Solde</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {balance.balance.map((r) => (
                      <TableRow key={r.code} data-testid={`balance-row-${r.code}`}>
                        <TableCell className="font-mono text-xs">{r.code}</TableCell>
                        <TableCell>{r.name}</TableCell>
                        <TableCell className="text-right"><Money value={r.debit} cur={cur} /></TableCell>
                        <TableCell className="text-right"><Money value={r.credit} cur={cur} /></TableCell>
                        <TableCell className="text-right font-semibold"><Money value={r.balance} cur={cur} /></TableCell>
                      </TableRow>))}
                    <TableRow className="font-display font-bold bg-secondary/50">
                      <TableCell colSpan={2}>Totaux</TableCell>
                      <TableCell className="text-right"><Money value={balance.total_debit} cur={cur} /></TableCell>
                      <TableCell className="text-right"><Money value={balance.total_credit} cur={cur} /></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
